import { Loader2, Download, ChevronLeft, ChevronRight, Check, Filter, BookOpen, Users, GraduationCap, ClipboardList } from 'lucide-react';
import DepartmentTreeSelect from "../selectors/DepartmentTreeSelect";
import React, { useState, useEffect, useCallback } from "react";
import checkPermission from "../../../../utils/permission";
import { ApiService } from "../../../../services/api";

interface Week {
  id: number;
  name: string;
  value: string;
  startDate: string;
  endDate: string;
}

interface Department {
  id: string;
  name: string;
  color?: string;
}

interface WeekData {
  assigned: number;
  completed: number;
  successRate: number;
  weekNumber: number;
}

interface ReportUser {
  id: string;
  firstName: string;
  lastName: string;
  schoolNumber?: string;
  department?: Department;
}

interface ReportRow {
  user: ReportUser;
  weekData: { [key: string]: WeekData };
}

interface LessonAverage {
  id: string;
  title: string;
  uniqueAssigned: number;
  totalAssigned: number;
  completed: number;
  completionRate: number;
  successRate: number;
}

interface ClassAverage {
  uniqueAssigned: number;
  totalAssigned: number;
  completed: number;
  completionRate: number;
  successRate: number;
  studentCount: number;
}

interface TeacherHomework {
  teacher: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
  };
  lesson: {
    id: string;
    title: string;
  };
  homeworkCount: number;
  successRate: number;
}

interface Statistics {
  lessonAverages: LessonAverage[];
  classAverage: ClassAverage;
  teacherHomeworks: TeacherHomework[];
}

interface ReportData {
  data: ReportRow[];
  weeks: Array<{
    weekNumber: number;
    name: string;
    dateRange: string;
  }>;
  meta: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    perPage: number;
  };
  statistics?: Statistics;
}

export default function DepartmentHomeworkReportTab() {
  const hasExportPermission = checkPermission("department_homework_report_export");

  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [availableWeeks, setAvailableWeeks] = useState<Week[]>([]);
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingWeeks, setLoadingWeeks] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filtered, setFiltered] = useState(false);
  const limit = 20;

  useEffect(() => {
    if (!selectedDepartments || selectedDepartments.length === 0) {
      setAvailableWeeks([]);
      setSelectedWeeks([]);
      setReportData(null);
      return;
    }

    const loadWeeks = async () => {
      setLoadingWeeks(true);
      setError(null);
      try {
        const response = await ApiService.getDepartmentWeeks(selectedDepartments[0]);
        setAvailableWeeks(response || []);
      } catch (err: any) {
        console.error("Error loading weeks:", err);
        setError(err?.message || "Haftalar yüklenemedi");
        setAvailableWeeks([]);
      } finally {
        setLoadingWeeks(false);
      }
    };

    loadWeeks();
  }, [selectedDepartments]);

  const loadReport = useCallback(
    async (currentPage: number = 1) => {
      if (!selectedDepartments || selectedDepartments.length === 0 || selectedWeeks.length === 0) {
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await ApiService.getDepartmentHomeworkReport(selectedDepartments[0], {
          weekNumbers: selectedWeeks,
          page: currentPage,
          limit,
          countMode: 'individual',
        });
        setReportData(response);
        setPage(currentPage);
      } catch (err: any) {
        console.error("Error loading report:", err);
        setError(err?.message || "Rapor yüklenemedi");
        setReportData(null);
      } finally {
        setLoading(false);
      }
    },
    [selectedDepartments, selectedWeeks, limit]
  );

  const handleExport = async () => {
    if (!selectedDepartments || selectedDepartments.length === 0 || selectedWeeks.length === 0) {
      return;
    }

    setExporting(true);
    try {
      const blob = await ApiService.exportDepartmentHomeworkReport(selectedDepartments[0], selectedWeeks, 'individual');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const weekStr = selectedWeeks.length > 1 ? `${selectedWeeks[0]}-${selectedWeeks[selectedWeeks.length - 1]}` : selectedWeeks[0];
      link.setAttribute("download", `sube_odev_raporu_${weekStr}_hafta_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error exporting report:", err);
      setError(err?.message || "Rapor dışa aktarılamadı");
    } finally {
      setExporting(false);
    }
  };

  const isFilterReady = () => {
    return selectedDepartments && selectedDepartments.length > 0 && selectedWeeks.length > 0;
  };

  const buildMissingMessage = () => {
    const parts: string[] = [];
    if (!selectedDepartments || selectedDepartments.length === 0) parts.push("Departman");
    if (selectedWeeks.length === 0) parts.push("Hafta");
    if (parts.length === 0) return "";
    if (parts.length === 1) return `${parts[0]} seçiniz.`;
    return `${parts[0]} ve ${parts[1]} seçiniz.`;
  };

  const handleFilter = () => {
    if (isFilterReady()) {
      setFiltered(true);
      loadReport(1);
    }
  };

  const handleClearFilters = () => {
    setSelectedDepartments([]);
    setSelectedWeeks([]);
    setReportData(null);
    setError(null);
    setFiltered(false);
  };

  return (
    <div className="h-full flex flex-col">
      {!filtered && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6 overflow-visible">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filtreler</h3>
          </div>

          <div className="overflow-visible">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 overflow-visible">
              <div className="col-span-1">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">Departman</label>
                <DepartmentTreeSelect
                  value={selectedDepartments}
                  onChange={(depts) => {
                    setSelectedDepartments(depts);
                    setSelectedWeeks([]);
                    setReportData(null);
                    setFiltered(false);
                  }}
                  multiple={false}
                  disableRootSelection={true}
                  placeholder="Departman Seçiniz"
                />
              </div>

              <div className="col-span-1">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  Hafta {selectedWeeks.length > 0 && `(${selectedWeeks.length} Seçili)`}
                </label>
                <div>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="max-h-64 overflow-auto divide-y divide-gray-200">
                      {loadingWeeks ? (
                        <div className="px-3 py-2 text-sm text-gray-500 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Yükleniyor...
                        </div>
                      ) : !selectedDepartments || selectedDepartments.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Hafta yok.</div>
                      ) : availableWeeks.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">Hafta yok.</div>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setSelectedWeeks([])}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedWeeks.length === 0 ? "bg-blue-50 text-blue-700 font-medium" : "text-gray-600"
                              }`}
                            disabled={!selectedDepartments || selectedDepartments.length === 0}>
                            Hafta Seçiniz
                          </button>
                          {availableWeeks.map((week) => {
                            const isSelected = selectedWeeks.includes(week.id);
                            const limitReached = !isSelected && selectedWeeks.length >= 4;
                            return (
                              <button
                                key={week.id}
                                type="button"
                                onClick={() => {
                                  const weekSet = new Set(selectedWeeks);
                                  if (weekSet.has(week.id)) {
                                    weekSet.delete(week.id);
                                  } else {
                                    if (weekSet.size >= 4) {
                                      setError("En fazla 4 hafta seçebilirsiniz.");
                                      return;
                                    }
                                    weekSet.add(week.id);
                                  }
                                  setError(null);
                                  setSelectedWeeks(Array.from(weekSet).sort((a, b) => a - b));
                                }}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${isSelected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-50 text-gray-800"
                                  } ${limitReached ? "opacity-50 cursor-not-allowed" : ""}`}
                                disabled={!selectedDepartments || selectedDepartments.length === 0 || limitReached}>
                                <span>
                                  <span className="font-medium">{week.name}</span>
                                  <span className="text-xs text-gray-500 ml-2">({week.value})</span>
                                </span>
                                {isSelected && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                              </button>
                            );
                          })}
                        </>
                      )}
                    </div>
                    <div className="px-3 pt-2 pb-1 text-xs text-gray-500">
                      En fazla 4 hafta seçebilirsiniz.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 mt-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleFilter}
                  disabled={!isFilterReady()}
                  className={`px-6 py-2 rounded-lg font-medium text-sm transition-colors ${isFilterReady() ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"
                    }`}>
                  Filtrele
                </button>
                {!isFilterReady() && (
                  <span className="text-xs text-gray-600">Raporları görüntülemek için lütfen {buildMissingMessage()}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {filtered && (
        <div className="grid grid-cols-1 md:grid-cols-3 items-center mb-6 gap-4">
          <div className="flex items-center space-x-4 justify-start">
            <div className="flex flex-row flex-wrap gap-2">
              <button
                onClick={handleClearFilters}
                className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 bg-white text-gray-700 hover:bg-gray-50 border-gray-300 transition-colors">
                <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Filtrele</span>
              </button>
              {reportData && hasExportPermission && (
                <button
                  onClick={handleExport}
                  disabled={exporting}
                  className="px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium bg-orange-600 text-white hover:bg-orange-700 border border-orange-600 flex-shrink-0 inline-flex items-center justify-center gap-1.5 sm:gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                  {exporting ? (
                    <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  )}
                  <span>{exporting ? "İndiriliyor..." : "Dışa Aktar"}</span>
                </button>
              )}
            </div>
          </div>
          <div />
          {reportData && (
            <div className="flex items-center space-x-4 justify-start md:justify-end">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>
                  {reportData.meta.totalItems > 0
                    ? `${(page - 1) * limit + 1}-${Math.min(page * limit, reportData.meta.totalItems)} / ${reportData.meta.totalItems}`
                    : "0 / 0"}
                </span>
                <button
                  onClick={() => loadReport(page - 1)}
                  disabled={page <= 1 || loading}
                  className="disabled:opacity-50 disabled:cursor-not-allowed">
                  <ChevronLeft className="h-4 w-4 text-gray-400" />
                </button>
                <button
                  onClick={() => loadReport(page + 1)}
                  disabled={page >= reportData.meta.totalPages || loading}
                  className="disabled:opacity-50 disabled:cursor-not-allowed">
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {filtered && error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm font-medium text-red-900">Hata</p>
          <p className="text-sm text-red-700 mt-1">{error}</p>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {!filtered ? (
          <div className="flex-1 flex items-center justify-center h-full"></div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Rapor yükleniyor...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Hata Oluştu</h3>
            <p className="text-gray-500 max-w-md mb-4">{error}</p>
            <button
              onClick={() => {
                setError(null);
                handleFilter();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Tekrar Dene
            </button>
          </div>
        ) : reportData && reportData.data.length > 0 ? (
          <>
            {reportData.statistics && (
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-3 sm:p-5">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900">Genel Sınıf Ortalaması</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Toplam Öğrenci</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900">{reportData.statistics.classAverage.studentCount}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Benzersiz Ödev Sayısı</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-600">{reportData.statistics.classAverage.uniqueAssigned}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Toplam Ödev Sayısı</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">{reportData.statistics.classAverage.totalAssigned}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Tamamlanma Oranı</p>
                    <p
                      className={`text-xl sm:text-2xl font-bold ${reportData.statistics.classAverage.completionRate >= 80
                        ? "text-green-600"
                        : reportData.statistics.classAverage.completionRate >= 50
                          ? "text-orange-500"
                          : "text-red-600"
                        }`}>
                      %{reportData.statistics.classAverage.completionRate}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Başarı Oranı</p>
                    <p
                      className={`text-xl sm:text-2xl font-bold ${reportData.statistics.classAverage.successRate >= 70
                        ? "text-green-600"
                        : reportData.statistics.classAverage.successRate >= 50
                          ? "text-orange-500"
                          : "text-red-600"
                        }`}>
                      %{reportData.statistics.classAverage.successRate}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3 italic">
                  * Bu istatistikler tüm {reportData.statistics.classAverage.studentCount} öğrenci üzerinden hesaplanmıştır.
                  Toplam {reportData.statistics.classAverage.totalAssigned} ödevden {reportData.statistics.classAverage.completed} tanesi tamamlanmıştır.
                </p>
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-5 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="w-5 h-5 text-orange-600" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Sınıf Ödev Bilgileri</h3>
              </div>
              <div className="block md:hidden divide-y divide-gray-200">
                {reportData.data.map((row) => (
                  <div key={row.user.id} className="py-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">
                          {row.user.firstName} {row.user.lastName}
                        </p>
                        <p className="text-xs text-gray-600">{row.user.department?.name || "—"}</p>
                        <p className="text-xs text-gray-500">No: {row.user.schoolNumber || "—"}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {reportData.weeks.map((week) => {
                        const weekData = row.weekData[`week_${week.weekNumber}`];
                        const assigned = weekData?.assigned || 0;
                        const completed = weekData?.completed || 0;
                        return (
                          <div key={week.weekNumber} className="flex items-center justify-between py-2 border-t border-gray-100 gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{week.name}</p>
                              <p className="text-xs text-gray-500 truncate">{week.dateRange}</p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <div className="text-center">
                                <p className="text-xs text-gray-500">Verilen</p>
                                <p className="text-sm font-semibold text-gray-700">{assigned}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-xs text-gray-500">Yapılan</p>
                                <p
                                  className={`text-sm font-semibold ${assigned === 0
                                    ? "text-gray-700"
                                    : completed === 0
                                      ? "text-red-600"
                                      : completed === assigned
                                        ? "text-green-600"
                                        : "text-orange-500"
                                    }`}>
                                  {completed}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sınıf</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Numara</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adı Soyadı</th>
                      {reportData.weeks.map((week) => (
                        <th
                          key={week.weekNumber}
                          colSpan={3}
                          className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-300">
                          {week.name}
                          <div className="text-xs font-normal text-gray-400 normal-case mt-1">{week.dateRange}</div>
                        </th>
                      ))}
                    </tr>
                    <tr className="bg-gray-100">
                      <th colSpan={3}></th>
                      {reportData.weeks.map((week) => (
                        <React.Fragment key={`header-${week.weekNumber}`}>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600 border-l border-gray-300">Verilen</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Yapılan</th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-600">Başarı</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.data.map((row, idx) => (
                      <tr key={row.user.id} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.user.department?.name || "—"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.user.schoolNumber || "—"}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {row.user.firstName} {row.user.lastName}
                        </td>
                        {reportData.weeks.map((week) => {
                          const weekData = row.weekData[`week_${week.weekNumber}`];
                          const assigned = weekData?.assigned || 0;
                          const completed = weekData?.completed || 0;
                          const success = weekData?.successRate || 0;

                          return (
                            <React.Fragment key={`${row.user.id}-week-${week.weekNumber}`}>
                              <td className="px-2 py-4 whitespace-nowrap text-sm text-center text-gray-700 border-l border-gray-300">
                                {assigned}
                              </td>
                              <td className="px-2 py-4 whitespace-nowrap text-sm text-center">
                                <span
                                  className={`font-semibold ${(() => {
                                    if (assigned === 0) return "text-gray-700";
                                    if (completed === 0) return "text-red-600";
                                    if (completed === assigned) return "text-green-600";
                                    return "text-orange-500";
                                  })()}`}>
                                  {completed}
                                </span>
                              </td>
                              <td className="px-2 py-4 whitespace-nowrap text-sm text-center">
                                {assigned > 0 && completed > 0 ? (
                                  <span
                                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${success >= 70
                                      ? "bg-green-100 text-green-800"
                                      : success >= 50
                                        ? "bg-yellow-100 text-yellow-800"
                                        : "bg-red-100 text-red-800"
                                      }`}>
                                    %{success}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 text-xs">-</span>
                                )}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {reportData.statistics && (
              <div className="space-y-6">
                {reportData.statistics.lessonAverages.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">Ders Bazlı Ortalamalar</h3>
                    </div>

                    <div className="block md:hidden space-y-3">
                      {reportData.statistics.lessonAverages.map((lesson) => (
                        <div key={lesson.id} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <h4 className="font-semibold text-gray-900 mb-3 text-base">{lesson.title}</h4>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-500 mb-1">Benzersiz Ödev</p>
                              <p className="text-base font-semibold text-purple-700">{lesson.uniqueAssigned}</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-500 mb-1">Toplam Ödev</p>
                              <p className="text-base font-semibold text-blue-700">{lesson.totalAssigned}</p>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-500 mb-1">Tamamlanma</p>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${lesson.completionRate >= 80
                                  ? "bg-green-100 text-green-800"
                                  : lesson.completionRate >= 50
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                                  }`}>
                                %{lesson.completionRate}
                              </span>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-500 mb-1">Başarı</p>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${lesson.successRate >= 70
                                  ? "bg-green-100 text-green-800"
                                  : lesson.successRate >= 50
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                                  }`}>
                                %{lesson.successRate}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ders</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Benzersiz Verilen Ödev</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ödev x Öğrenci Verilen Ödev</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Tamamlanma Oranı</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Başarı Oranı</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {reportData.statistics.lessonAverages.map((lesson) => (
                            <tr key={lesson.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{lesson.title}</td>
                              <td className="px-4 py-3 text-sm text-center text-purple-700 font-semibold">{lesson.uniqueAssigned}</td>
                              <td className="px-4 py-3 text-sm text-center text-blue-700 font-semibold">{lesson.totalAssigned}</td>
                              <td className="px-4 py-3 text-sm text-center">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${lesson.completionRate >= 80
                                    ? "bg-green-100 text-green-800"
                                    : lesson.completionRate >= 50
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                    }`}>
                                  %{lesson.completionRate}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-center">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${lesson.successRate >= 70
                                    ? "bg-green-100 text-green-800"
                                    : lesson.successRate >= 50
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                    }`}>
                                  %{lesson.successRate}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {reportData.statistics.teacherHomeworks.length > 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <GraduationCap className="w-5 h-5 text-teal-600" />
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900">Öğretmen Bazlı Ödev Sayıları</h3>
                    </div>

                    <div className="block md:hidden space-y-3">
                      {reportData.statistics.teacherHomeworks.map((item, idx) => (
                        <div key={`${item.teacher.id}-${item.lesson.id}-${idx}`} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                          <div className="mb-3">
                            <p className="font-semibold text-gray-900 text-base">{item.teacher.fullName}</p>
                            <p className="text-sm text-gray-600 mt-1">{item.lesson.title}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-500 mb-1">Ödev Sayısı</p>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                                {item.homeworkCount}
                              </span>
                            </div>
                            <div className="bg-white rounded p-2">
                              <p className="text-xs text-gray-500 mb-1">Başarı Oranı</p>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${item.successRate >= 70
                                  ? "bg-green-100 text-green-800"
                                  : item.successRate >= 50
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-red-100 text-red-800"
                                  }`}>
                                %{item.successRate}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="hidden md:block overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Öğretmen</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ders</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Benzersiz Gönderilen Ödev</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Başarı Oranı</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {reportData.statistics.teacherHomeworks.map((item, idx) => (
                            <tr key={`${item.teacher.id}-${item.lesson.id}-${idx}`} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.teacher.fullName}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">{item.lesson.title}</td>
                              <td className="px-4 py-3 text-sm text-center">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-100 text-purple-800">
                                  {item.homeworkCount}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-center">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.successRate >= 70
                                    ? "bg-green-100 text-green-800"
                                    : item.successRate >= 50
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                    }`}>
                                  %{item.successRate}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Veri Bulunamadı</h3>
            <p className="text-gray-500 max-w-md mb-4">Seçilen kriterlere uygun rapor verisi bulunamadı.</p>
            <button onClick={handleFilter} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Yenile
            </button>
          </div>
        )}
      </div>
    </div>
  );
}